import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { api } from "../../api";
import PageHeader from "../../components/PageHeader";
import StockLogNote from "../../components/StockLogNote";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";

const REASONS = [
  { key: "__all__", label: "All reasons" },
  { key: "order", label: "Order" },
  { key: "manual", label: "Manual" },
  { key: "csv_import", label: "CSV import" },
];

export default function StockLogs() {
  const [params, setParams] = useSearchParams();
  const variantIdParam = params.get("size_variant_id") || "";
  const productIdParam = params.get("product_id") || "";

  const [logs, setLogs] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reason, setReason] = useState("__all__");
  const [productId, setProductId] = useState(productIdParam || "__all__");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({ limit: "500" });
    if (reason !== "__all__") p.set("reason", reason);
    if (variantIdParam) p.set("size_variant_id", variantIdParam);
    else if (productId !== "__all__") p.set("product_id", productId);
    try { const { data } = await api.get(`/stock-logs?${p.toString()}`); setLogs(data); }
    finally { setLoading(false); }
  }, [reason, productId, variantIdParam]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)); }, []);
  // Load orders once so we can resolve DB-YYYY-NNNNNN notes to clickable links.
  useEffect(() => { api.get("/admin/orders").then((r) => setOrders(r.data)); }, []);

  const productName = (id) => products.find((p) => p.id === id)?.name || "—";
  const ordersByNumber = useMemo(() => {
    const m = {};
    for (const o of orders) if (o.order_number) m[o.order_number] = o;
    return m;
  }, [orders]);

  const clearVariantFilter = () => {
    const next = new URLSearchParams(params);
    next.delete("size_variant_id");
    setParams(next, { replace: true });
  };

  return (
    <>
      <PageHeader title="Stock log" subtitle="Every stock change flows through the central inventory service." testid="page-stock-logs" />
      <div className="p-6">
        {variantIdParam && (
          <div className="mb-3 flex items-center gap-2 bg-[#E9F5EC] border border-[#BFDBC5] rounded-md px-3 py-1.5 text-xs text-[#1B4332]" data-testid="stock-log-variant-chip">
            <span>Filtered to a single size variant.</span>
            <button
              onClick={clearVariantFilter}
              className="ml-auto inline-flex items-center gap-1 hover:underline"
              data-testid="stock-log-clear-variant"
            >
              <X size={12} /> Clear
            </button>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger className="h-9 w-40" data-testid="stock-log-reason"><SelectValue /></SelectTrigger>
            <SelectContent>{REASONS.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={productId} onValueChange={setProductId} disabled={!!variantIdParam}>
            <SelectTrigger className="h-9 w-64" data-testid="stock-log-product"><SelectValue placeholder="Product" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All products</SelectItem>
              {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="ml-auto text-xs text-[#5C665A]" data-testid="stock-log-count">{logs.length} entries</div>
        </div>

        <div className="bg-white border border-[#E2E4E0] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-[#F9F9F8] text-[#5C665A]">
              <tr>
                <th className="text-left px-3 py-2 db-h font-semibold">When</th>
                <th className="text-left px-3 py-2 db-h font-semibold">Product</th>
                <th className="text-left px-3 py-2 db-h font-semibold w-32">Size</th>
                <th className="text-left px-3 py-2 db-h font-semibold">Reason</th>
                <th className="text-right px-3 py-2 db-h font-semibold">Δ</th>
                <th className="text-right px-3 py-2 db-h font-semibold">New qty</th>
                <th className="text-left px-3 py-2 db-h font-semibold">Note</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-3 py-8 text-center text-[#5C665A]" data-testid="stock-log-loading">Loading…</td></tr>}
              {!loading && logs.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-[#5C665A]" data-testid="stock-log-empty">No log entries.</td></tr>}
              {logs.map((l) => (
                <tr key={l.id} className="border-t border-[#E2E4E0]" data-testid={`stock-log-row-${l.id}`}>
                  <td className="px-3 py-1.5 text-xs text-[#5C665A] db-mono">{new Date(l.timestamp).toLocaleString()}</td>
                  <td className="px-3 py-1.5">{productName(l.product_id)}</td>
                  <td className="px-3 py-1.5" data-testid={`stock-log-size-${l.id}`}>
                    {l.size_name ? (
                      <span className="inline-flex items-center gap-1">
                        <span className="text-[#1A1D1A]">{l.size_name}</span>
                        {l.sku && <span className="text-[11px] text-[#5C665A] db-mono">· {l.sku}</span>}
                      </span>
                    ) : (
                      <span className="text-xs text-[#8A9088]">—</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5">
                    <Badge className={l.reason === "order" ? "bg-[#E9F5EC] text-[#1B4332] border-transparent" : "bg-neutral-100 text-neutral-600 border-transparent"}>{l.reason}</Badge>
                  </td>
                  <td className={`px-3 py-1.5 text-right db-mono ${l.delta < 0 ? "text-[#C94A4A]" : "text-[#40916C]"}`}>{l.delta > 0 ? "+" : ""}{l.delta}</td>
                  <td className="px-3 py-1.5 text-right db-mono">{l.new_qty}</td>
                  <td className="px-3 py-1.5 text-xs text-[#5C665A]"><StockLogNote note={l.note || ""} ordersByNumber={ordersByNumber} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </div>
    </>
  );
}
